import stockLogoManifest from "../UtilityJsons/StockLogoManifest.json";

export const formatBigNumbers = (number: number | null): string => {
  if (number === null || number === undefined) return "N/A";

  const isNegative = number < 0;
  const absoluteNumber = Math.abs(number);

  if (absoluteNumber >= 1e12)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e12).toFixed(2)}\u200AT$`; // Trillions
  if (absoluteNumber >= 1e9)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e9).toFixed(2)}\u200AB$`; // Billions
  if (absoluteNumber >= 1e6)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e6).toFixed(2)}\u200AM$`; // Millions
  if (absoluteNumber >= 1e3)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e3).toFixed(2)}\u200AK$`; // Thousands
  return `${isNegative ? "-" : ""} ${absoluteNumber.toFixed(2)}$`; // Less than 1,000
};

export const formatBigNumbersNoDecimals = (number: number | null): string => {
  if (number === null || number === undefined) return "N/A";

  const isNegative = number < 0;
  const absoluteNumber = Math.abs(number);

  const format = (value: number, suffix: string) => {
    const formatted = value.toFixed(2);
    return `${isNegative ? "-" : ""} ${formatted.endsWith(".00") ? formatted.slice(0, -3) : formatted} ${suffix}`;
  };

  if (absoluteNumber >= 1e12) return format(absoluteNumber / 1e12, "T$");
  if (absoluteNumber >= 1e9) return format(absoluteNumber / 1e9, "B$");
  if (absoluteNumber >= 1e6) return format(absoluteNumber / 1e6, "M$");
  if (absoluteNumber >= 1e3) return format(absoluteNumber / 1e3, "K$");
  return format(absoluteNumber, "$");
};

export const formatBigNumbersNoDollar = (number: number | null): string => {
  if (number === null || number === undefined) return "N/A";

  const isNegative = number < 0;
  const absoluteNumber = Math.abs(number);

  if (absoluteNumber >= 1e12)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e12).toFixed(
      2
    )}\u00A0T`; // Trillions
  if (absoluteNumber >= 1e9)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e9).toFixed(
      2
    )}\u00A0B`; // Billions
  if (absoluteNumber >= 1e6)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e6).toFixed(
      2
    )}\u00A0M`; // Millions
  if (absoluteNumber >= 1e3)
    return `${isNegative ? "-" : ""} ${(absoluteNumber / 1e3).toFixed(
      2
    )}\u00A0K`; // Thousands
  return `${isNegative ? "-" : ""} ${absoluteNumber.toFixed(2)}`; // Less than 1,000
};

const normalizedTicker = (ticker: string) => ticker.trim().toUpperCase();
const iconLogoTickers = new Set<string>(stockLogoManifest.icon);
const fullLogoTickers = new Set<string>(stockLogoManifest.full);
const STOCK_LOGO_VERSION = "3";

const staticLogoPath = (
  kind: "full" | "icon" | "full-72" | "icon-72",
  ticker: string
) =>
  `/stock-logos/${kind}/${encodeURIComponent(
    normalizedTicker(ticker)
  )}.png?v=${STOCK_LOGO_VERSION}`;

const remoteLogoPath = (logoUrl?: string) =>
  logoUrl ? `https://eodhd.com/${logoUrl.replace(/^\/+/, "")}` : "";

const tickerPlaceholder = (ticker: string) => {
  const label = normalizedTicker(ticker).split(".")[0].slice(0, 4) || "?";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="20" fill="#edf4f1"/><text x="48" y="54" text-anchor="middle" font-family="Arial,sans-serif" font-size="22" font-weight="700" fill="#315c52">${label}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export const getFullImagePath = (_ticker: string, logoUrl?: string): string => {
  const ticker = normalizedTicker(_ticker);
  if (fullLogoTickers.has(ticker)) return staticLogoPath("full", ticker);
  if (iconLogoTickers.has(ticker)) return staticLogoPath("icon", ticker);
  return remoteLogoPath(logoUrl) || tickerPlaceholder(ticker);
};

export const getIconImagePath = (_ticker: string, logoUrl?: string): string => {
  const ticker = normalizedTicker(_ticker);
  if (iconLogoTickers.has(ticker)) return staticLogoPath("icon", ticker);
  if (fullLogoTickers.has(ticker)) return staticLogoPath("full", ticker);
  return remoteLogoPath(logoUrl) || tickerPlaceholder(ticker);
};

export const getCompactLogoPath = (
  _ticker: string,
  logoUrl?: string
): string => {
  const ticker = normalizedTicker(_ticker);
  if (iconLogoTickers.has(ticker)) return staticLogoPath("icon-72", ticker);
  if (fullLogoTickers.has(ticker)) return staticLogoPath("full-72", ticker);
  return remoteLogoPath(logoUrl) || tickerPlaceholder(ticker);
};

export const hasLocalLogoForTicker = (ticker: string): boolean =>
  iconLogoTickers.has(normalizedTicker(ticker)) ||
  fullLogoTickers.has(normalizedTicker(ticker));
